import DiagramBlockContext from '@/contexts/DiagramBlockContext';
import RealtimeDataContext from '@/contexts/RealtimeDataContext';
import { getResumeFromDevice } from '@/services/measurement';
import { infraTypesByIdForIcons } from '@/statics/constants/infraTypes';
import { formatRelativeTime } from '@/utils/dateTimeUtils';
import Icon, { PlusOutlined } from '@ant-design/icons';
import { HealthIndicator } from '@hydrodigital/design-system';
import type { MenuProps } from 'antd';
import { Dropdown, Modal, Tooltip } from 'antd';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Handle, Position } from 'reactflow';
import HistorySvg from '../../../assets/svg/HistorySvg';
import ThreeDotsSVG from '../../../assets/svg/ThreeDotsSVG';
import WaveSvg from '../../../assets/svg/WaveSvg';
import ListParametersTree from '../../Parameters/Infrastructure/listParametersTree';
import './ParameterNode.scss';

const ParameterNode: React.FC = ({ data, id, selected }: any) => {
	const { value: context, setValue: setContext } = useContext(DiagramBlockContext);
	const { realtimeEnabled, getNodeValue } = useContext(RealtimeDataContext);
	const [showModal, setShowModal] = useState(false);
	const [resumeInfo, setResumeInfo] = useState<any>(null);
	const [lastValidValue, setLastValidValue] = useState<number | null>(null);
	const globalDate = localStorage.getItem('globalDate');
	const [dateFrom, dataTo] = globalDate ? globalDate.split('***') : [];

	const items: MenuProps['items'] = [
		{
			label: (
				<div
					className="flex"
					onClick={() => {
						data.addNode(id);
					}}
				>
					<PlusOutlined className="mr-2" rev={undefined} />
					<span>Create new node</span>
				</div>
			),
			key: '0',
		},
		{
			type: 'divider',
		},
		{
			label: (
				<span
					style={{ color: '#F46A6A', cursor: 'pointer' }}
					onClick={() => {
						data.removeNode(id);
					}}
				>
					Delete
				</span>
			),
			key: '1',
		},
	];

	async function handleGetReading() {
		const resume = await getResumeFromDevice(
			data.value.infrastructureId,
			data.value.paremeterId,
			null,
			dateFrom,
			dataTo,
		);
		setResumeInfo(resume);
	}

	
	const displayValue = useMemo(() => {
		if (!resumeInfo) {
			return undefined;
		}

		if (realtimeEnabled) {
			const realtimeValue = getNodeValue(id);

			
			if (realtimeValue !== null) {
				setLastValidValue(realtimeValue);
				return realtimeValue;
			}

			
			return '-No Data-';
		}

		
		const currentValue = resumeInfo.currentValue;
		if (currentValue !== null && currentValue !== undefined) {
			setLastValidValue(currentValue);
		}
		return currentValue;
	}, [realtimeEnabled, resumeInfo, getNodeValue, id, lastValidValue]);

	const HealthIndicatorWrapper = useMemo(() => {
		if (!resumeInfo || displayValue === undefined) return null;

		const { criticalLlow, criticalLow, criticalHigh, criticalHhigh } = resumeInfo;

		const isValidNumber = (value: unknown): value is number => {
			return typeof value === 'number' && !isNaN(value) && isFinite(value);
		};

		if (
			!isValidNumber(criticalLlow) ||
			!isValidNumber(criticalLow) ||
			!isValidNumber(criticalHigh) ||
			!isValidNumber(criticalHhigh)
		) {
			return null;
		}

		const isUsingRealtimeData = realtimeEnabled && resumeInfo;

		const currentValue =
			displayValue === '-No Data-' || displayValue === null || displayValue === undefined
				? null
				: Number(displayValue);

		const unitValue =
			displayValue !== '-No Data-' && resumeInfo && 'unit' in resumeInfo && resumeInfo.unit
				? String(resumeInfo.unit)
				: '';

		const lowLowRange = criticalLlow !== criticalLow ? criticalLlow : undefined;
		const highHighRange = criticalHhigh !== criticalHigh ? criticalHhigh : undefined;

		return (
			<HealthIndicator
				currentValue={currentValue}
				lowLowRange={lowLowRange}
				lowRange={criticalLow}
				highRange={criticalHigh}
				highHighRange={highHighRange}
				unitValue={`${unitValue} ${isUsingRealtimeData ? '(RT)' : ''}`}
			/>
		);
	}, [resumeInfo, displayValue, realtimeEnabled]);

	
	const infrastructureIcon = useMemo(() => {
		const infrastructureType = resumeInfo?.infrastructureType;

		if (!infrastructureType) return WaveSvg;

		
		const infraTypeEntries = Object.entries(infraTypesByIdForIcons);
		const matchingEntry = infraTypeEntries.find(([id, iconName]) => {
			
			const normalizedType = infrastructureType.toUpperCase().replace(/\s+/g, '');
			const normalizedIconName = iconName.toUpperCase();

			return (
				normalizedType.includes(normalizedIconName) ||
				normalizedIconName.includes(normalizedType) ||
				(normalizedType === 'RAINGAUGE' && normalizedIconName === 'RAINGAURGE') ||
				(normalizedType === 'STORAGE' && normalizedIconName === 'STORAGETANK') ||
				(normalizedType === 'TANK' && normalizedIconName === 'STORAGETANK') ||
				(normalizedType === 'PIPE' && normalizedIconName === 'CONDUIT') ||
				(normalizedType === 'RESERVOIRS' && normalizedIconName === 'RESERVOIR')
			);
		});

		if (matchingEntry) {
			const iconName = matchingEntry[1];
			const cdnBase = import.meta.env.VITE_AZURE_CDN;

			
			return () => (
				<img
					src={`${cdnBase}img/InfraIcons/svg/${iconName}.svg`}
					alt={infrastructureType}
					style={{
						width: '35px',
						height: '35px',
					}}
					className="mr-2"
				/>
			);
		}

		return WaveSvg;
	}, [resumeInfo?.infrastructureType]);

	useEffect(() => {
		if (data.value.infrastructureId && data.value.serialNumberSource) {
			handleGetReading();
		}
	}, [data.value]);

	useEffect(() => {
		if (
			resumeInfo?.currentValue !== null &&
			resumeInfo?.currentValue !== undefined &&
			lastValidValue === null
		) {
			setLastValidValue(resumeInfo.currentValue);
		}
	}, [resumeInfo, lastValidValue]);

	return (
		<>
			<div
				className={`w-[13.25rem] rounded-[8px] px-[16px] py-[8px] transition-colors ${
					selected ? 'bg-[#CCF1FF]' : 'bg-white'
				}`}
				style={{
					border: '0.0625rem solid #078CB3',
				}}
			>
				{resumeInfo ? (
					<>
						<div className="flex items-center">
							<div>{React.createElement(infrastructureIcon)}</div>
							<div className="flex justify-between w-full items-center">
								<div className="flex flex-col">
									<h2 className="text-xs text-black font-normal">
										{resumeInfo.infrastructureName}
									</h2>
									<h3 className="text-[10px] text-[#74788D] font-bold">
										{resumeInfo.infrastructureType}
									</h3>
								</div>
								<div>
									{data.editable && (
										<Dropdown menu={{ items }}>
											<Icon
												component={ThreeDotsSVG}
												style={{
													fontSize: '20px',
													cursor: 'pointer',
												}}
												rev={undefined}
											/>
										</Dropdown>
									)}
								</div>
							</div>
						</div>

						<hr className="border-[#E6E6E6] my-2" />
						<Tooltip title={resumeInfo.parameterName} placement="top">
							<h3 className="truncate text-sm font-medium text-gray-800 cursor-default">
								{resumeInfo.parameterName}
							</h3>
						</Tooltip>
						{HealthIndicatorWrapper}

						<div className="flex gap-4">
							<div>
								<Icon component={HistorySvg} color="#000000" className="w-2.5" rev={undefined} />
							</div>
							<div>
								<h3 className="text-black font-bold text-[10px] mt-[3px]">
									Updated {formatRelativeTime(resumeInfo.lastCollected)}
								</h3>
								<h3 className="text-black font-light text-[10px]">
									{resumeInfo.originSourceName} {!!resumeInfo.virtual && <span>(VIRTUAL)</span>}
								</h3>
							</div>
						</div>
					</>
				) : (
					<>
						<div className="flex items-center">
							<div>
								{resumeInfo ? (
									React.createElement(infrastructureIcon)
								) : (
									<Icon
										component={WaveSvg}
										style={{
											fontSize: '20px',
											cursor: 'pointer',
										}}
										className="mr-2"
									/>
								)}
							</div>
							<div className="flex justify-between w-full items-center">
								<div className="flex flex-col">
									<h2 className="text-xs text-black font-normal">{data.label}</h2>
								</div>
								<div>
									{data.editable && (
										<Dropdown menu={{ items }}>
											<Icon
												component={ThreeDotsSVG}
												style={{
													fontSize: '20px',
													cursor: 'pointer',
												}}
												rev={undefined}
											/>
										</Dropdown>
									)}
								</div>
							</div>
						</div>
						<hr className="border-[#E6E6E6] my-2" />
						<div className="flex column justify-center items-center">
							<h1 className="italic font-bold text-[20px] text-center px-4 py-2">
								{data.value.paremeterId
									? 'Loading data...'
									: 'Link this parameter node to an asset in Parameter Node'}
							</h1>
						</div>
					</>
				)}
				<Handle
					type="target"
					position={Position.Left}
					className="w-3 h-3 !bg-white !border-2 !border-black !rounded-full hover:!bg-blue-600 transition-colors"
					style={{ left: -5.5 }}
				/>
				<Handle
					type="source"
					position={Position.Right}
					className="w-3 h-3 !bg-white !border-2 !border-black !rounded-full hover:!bg-green-600 transition-colors"
					style={{ right: -5.5 }}
				/>
			</div>
			<Modal
				title="Select a parameter"
				open={showModal}
				footer={[]}
				onCancel={() => {
					setShowModal(false);
				}}
			>
				<ListParametersTree
					locationId={context.locationId}
					onSelect={(val: string) => {
						data.onChange(JSON.parse(val), id);
						setShowModal(false);
					}}
				/>
			</Modal>
		</>
	);
};

export default ParameterNode;
